import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { PanelComponent } from '@components/core/panel/panel.component';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectComponent } from "@ng-select/ng-select";
import { UserService } from '@services/core/user.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-user-menus',
  templateUrl: './menus.component.html',
  styleUrls: ["./menus.component.scss"],
  imports: [FormsModule, ReactiveFormsModule, NgbTooltipModule, PanelComponent, NgSelectComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class MenusComponent implements OnInit {

  private modalService = inject(NgbModal)
  private _fb = inject(FormBuilder)
  private _userService = inject(UserService)
  private destroyRef = inject(DestroyRef)
  private modalOptions = {
    size: 'xl',
    scrollable: true,
    animation: true,
    backdrop: null
  }
  public closeResult = ""

  protected menuForm = this._fb.group({
    id: [null],
    title: [null, Validators.required],
    icon: [null],
    url: [null],
    parentId: [null],
  })

  protected roles: any[] = [];
  protected roleSelected: string | null = null
  protected menus: any[] = []

  ngOnInit(): void {
    this._userService.getRoles().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((roles: any) => {
      this.roles = roles;
      if (roles.length > 0) {
        this.roleSelected = roles[0].id;
        this.loadMenusByRole();
      }
    });
  }

  onRoleChange() {
    if (this.roleSelected) {
      this.loadMenusByRole()
    }
  }

  loadMenusByRole() {
    this._userService.getMenuByRole(this.roleSelected!).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res: any) => {
      this.menus = res
    })
  }

  open(modal: any, menu?: any) {
    if (menu) this.menuForm.patchValue(menu)
    else this.menuForm.reset()
    this.modalService.open(modal, this.modalOptions).result.then((result) => {
      this.closeResult = `Closed with: ${result}`;
    }, () => {});
  }

  saveMenu() {
    if (this.menuForm.valid) {
      this._userService.updateMenu(this.menuForm.value).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.roleSelected) this.loadMenusByRole()
        this.menuForm.reset()
      })
    }
  }

  toggleMenuRole(menu: any, enabled: boolean) {
    this._userService.toggleMenuRole(this.roleSelected!, menu.id, enabled).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      menu.enabled = enabled
      if (menu.submenu) {
        menu.submenu.forEach((s: any) => {
          s.enabled = enabled
        })
      }
    })
  }

  moveUp(menu: any, index: number) {
    if (index === 0) return
    this.menus[index] = this.menus[index - 1]
    this.menus[index - 1] = menu
    this.saveOrder()
  }

  moveDown(menu: any, index: number) {
    if (index === this.menus.length - 1) return
    this.menus[index] = this.menus[index + 1]
    this.menus[index + 1] = menu
    this.saveOrder()
  }

  moveSubUp(parent: any, sub: any, index: number) {
    if (index === 0) return
    parent.submenu[index] = parent.submenu[index - 1]
    parent.submenu[index - 1] = sub
    this.saveOrder()
  }

  moveSubDown(parent: any, sub: any, index: number) {
    if (index === parent.submenu.length - 1) return
    parent.submenu[index] = parent.submenu[index + 1]
    parent.submenu[index + 1] = sub
    this.saveOrder()
  }

  private saveOrder() {
    const payload: any[] = []
    for (const menu of this.menus) {
      payload.push({ id: menu.id, index: payload.length })
      if (menu.submenu) {
        for (const sub of menu.submenu) {
          payload.push({ id: sub.id, index: payload.length })
        }
      }
    }
    this._userService.reorderMenus(payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe()
  }
}
